// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import './Pagination.css';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems?: number;
  itemsPerPage?: number;
  itemsPerPageOptions?: number[];
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (size: number) => void;
  className?: string;
}

export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage = 20,
  itemsPerPageOptions = [5, 10, 20, 50],
  onPageChange,
  onItemsPerPageChange,
  className = '',
}: PaginationProps) {
  const isFirst = currentPage === 0;
  const isLast = currentPage >= totalPages - 1;

  return (
    <div className={`fin-pagination ${className}`}>
      {onItemsPerPageChange && (
        <div className="fin-pagination__per-page">
          <span className="fin-pagination__label">Items per page:</span>
          <select
            className="fin-pagination__select"
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          >
            {itemsPerPageOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      )}

      <span className="fin-pagination__info">
        {totalPages > 0 ? `${currentPage + 1} of ${totalPages}` : '0 of 0'}
        {totalItems !== undefined && (
          <span className="fin-pagination__total"> ({totalItems} items)</span>
        )}
      </span>

      <div className="fin-pagination__buttons">
        <button
          className="fin-pagination__btn"
          disabled={isFirst}
          onClick={() => onPageChange(0)}
          aria-label="First page"
          title="First page"
        >
          ⏮
        </button>
        <button
          className="fin-pagination__btn"
          disabled={isFirst}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
          title="Previous page"
        >
          ◀
        </button>
        <button
          className="fin-pagination__btn"
          disabled={isLast}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
          title="Next page"
        >
          ▶
        </button>
        <button
          className="fin-pagination__btn"
          disabled={isLast}
          onClick={() => onPageChange(totalPages - 1)}
          aria-label="Last page"
          title="Last page"
        >
          ⏭
        </button>
      </div>
    </div>
  );
}
